import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, map, of, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { DeviceElement } from '../models/device.model';
import {
  AlarmPage,
  AlarmQuery,
  AlarmSummary,
  ApiEnvelope,
  DashboardDevice,
  DashboardSnapshot,
  DeviceTelemetryResponse,
  LatestTelemetryDevice,
  LatestTelemetryResponse,
} from '../models/dashboard.model';
import {
  buildDeviceStatus,
  deviceOperationalRank,
  deviceOperationalStatus,
  getNumericMeasure,
} from '../utils/telemetry-status';

const API_URL = environment.API_URL;
const LATEST_BATCH_SIZE = 100;

function chunks<T>(items: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size));
  }
  return result;
}

@Injectable({ providedIn: 'root' })
export class DashboardService {
  constructor(private http: HttpClient) {}

  getDevices(companyId: number): Observable<DeviceElement[]> {
    return this.http.get<ApiEnvelope<DeviceElement[]>>(`${API_URL}/companies/${companyId}/devices`).pipe(
      map(response => response.data ?? []),
    );
  }

  getLatestTelemetry(companyId: number, deviceIds: number[]): Observable<LatestTelemetryDevice[]> {
    const uniqueIds = [...new Set(deviceIds.map(Number).filter(id => Number.isInteger(id) && id > 0))];
    if (!uniqueIds.length) return of([]);

    return forkJoin(chunks(uniqueIds, LATEST_BATCH_SIZE).map(batch => {
      const params = new HttpParams().set('deviceIds', batch.join(','));
      return this.http.get<ApiEnvelope<LatestTelemetryResponse>>(
        `${API_URL}/companies/${companyId}/devices/latest-telemetry`,
        { params },
      ).pipe(map(response => response.data?.devices ?? []));
    })).pipe(map(responses => responses.flat()));
  }

  getAlarms(companyId: number, query: AlarmQuery = {}): Observable<AlarmPage> {
    let params = new HttpParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<ApiEnvelope<AlarmPage>>(`${API_URL}/companies/${companyId}/alarms`, { params }).pipe(
      map(response => response.data),
    );
  }

  acknowledgeAlarm(companyId: number, alarmId: string): Observable<AlarmSummary> {
    return this.http.post<ApiEnvelope<AlarmSummary>>(
      `${API_URL}/companies/${companyId}/alarms/${encodeURIComponent(alarmId)}/acknowledge`,
      {},
    ).pipe(map(response => response.data));
  }

  getDeviceTelemetry(
    companyId: number,
    deviceId: number,
    from: Date,
    to: Date,
    measures = 'device_state,state,temperature,battery,humidity',
  ): Observable<DeviceTelemetryResponse> {
    const params = new HttpParams()
      .set('from', from.toISOString())
      .set('to', to.toISOString())
      .set('measures', measures)
      .set('mode', 'raw');
    return this.http.get<ApiEnvelope<DeviceTelemetryResponse>>(
      `${API_URL}/companies/${companyId}/devices/${deviceId}/telemetry`,
      { params },
    ).pipe(map(response => response.data));
  }

  getSnapshot(companyId: number, projectId?: number): Observable<DashboardSnapshot> {
    const alarmQuery: AlarmQuery = { status: 'active', limit: 100 };
    if (projectId) alarmQuery.projectId = projectId;

    return forkJoin({
      devices: this.getDevices(companyId),
      alarms: this.getAlarms(companyId, alarmQuery),
    }).pipe(
      map(({ devices, alarms }) => ({
        devices: projectId ? devices.filter(device => Number(device.project_id) === Number(projectId)) : devices,
        alarms,
      })),
      switchMap(({ devices, alarms }) => this.getLatestTelemetry(companyId, devices.map(device => device.id)).pipe(
        map(latest => this.buildSnapshot(devices, latest, alarms.alarms)),
      )),
    );
  }

  getGlobalSnapshot(): Observable<DashboardSnapshot> {
    return this.getDevices(0).pipe(
      switchMap(devices => {
        const companyIds = [...new Set(devices.map(device => Number(device.company_id)).filter(Boolean))];
        if (!companyIds.length) return of(this.buildSnapshot([], [], []));
        return forkJoin({
          telemetry: forkJoin(companyIds.map(companyId => this.getLatestTelemetry(
            companyId,
            devices.filter(device => Number(device.company_id) === companyId).map(device => device.id),
          ))).pipe(map(groups => groups.flat())),
          alarms: forkJoin(companyIds.map(companyId => this.getAlarms(companyId, { status: 'active', limit: 100 })))
            .pipe(map(pages => pages.flatMap(page => page.alarms))),
        }).pipe(map(({ telemetry, alarms }) => this.buildSnapshot(devices, telemetry, alarms)));
      }),
    );
  }

  getGlobalAlarms(query: AlarmQuery): Observable<AlarmPage> {
    return this.getDevices(0).pipe(
      switchMap(devices => {
        const companyIds = [...new Set(devices.map(device => Number(device.company_id)).filter(Boolean))];
        if (!companyIds.length) return of({ companyId: 0, alarms: [], nextCursor: null });
        return forkJoin(companyIds.map(companyId => this.getAlarms(companyId, { ...query, cursor: undefined }))).pipe(
          map(pages => ({
            companyId: 0,
            alarms: pages.flatMap(page => page.alarms)
              .sort((left, right) => Date.parse(right.detectedAt) - Date.parse(left.detectedAt)),
            nextCursor: null,
          })),
        );
      }),
    );
  }

  private buildSnapshot(
    devices: DeviceElement[],
    latestTelemetry: LatestTelemetryDevice[],
    activeAlarms: AlarmSummary[],
  ): DashboardSnapshot {
    const telemetryByDevice = new Map(latestTelemetry.map(item => [Number(item.deviceId), item]));
    const alarmCountByDevice = new Map<number, number>();
    activeAlarms.forEach(alarm => {
      alarmCountByDevice.set(alarm.deviceId, (alarmCountByDevice.get(alarm.deviceId) ?? 0) + 1);
    });

    const dashboardDevices: DashboardDevice[] = devices.map(device => {
      const latest = telemetryByDevice.get(Number(device.id)) ?? null;
      const telemetryStatus = buildDeviceStatus(latest);
      return {
        ...device,
        latestTelemetry: latest,
        telemetryStatus,
        operationalStatus: deviceOperationalStatus(telemetryStatus),
        activeAlarmCount: alarmCountByDevice.get(Number(device.id)) ?? 0,
        temperature: getNumericMeasure(latest, 'temperature'),
        battery: getNumericMeasure(latest, 'battery'),
      };
    }).sort((left, right) => {
      const rank = deviceOperationalRank(left.operationalStatus)
        - deviceOperationalRank(right.operationalStatus);
      return rank || left.serial_number.localeCompare(right.serial_number);
    });

    return { devices: dashboardDevices, activeAlarms, loadedAt: new Date() };
  }
}
